import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import Seo, { buildBreadcrumbs } from "@/components/site/Seo";
import Markdown from "@/components/site/Markdown";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-slate-500" data-testid="blog-post-loading">
        Loading…
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="blog-post-notfound">
        <h2 className="font-display text-3xl font-bold text-slate-900">Post not found.</h2>
        <p className="mt-3 text-slate-600">It may have been moved or unpublished.</p>
        <button onClick={() => navigate("/blog")} className="btn-ghost mt-8 inline-flex">
          <ArrowLeft size={14} /> Back to blog
        </button>
      </div>
    );
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.hero_image || undefined,
      datePublished: post.created_at,
      dateModified: post.updated_at,
      author: { "@type": "Organization", name: post.author || "Prime Build Design" },
      publisher: {
        "@type": "Organization",
        name: "Prime Build Design LTD",
        logo: {
          "@type": "ImageObject",
          url: "https://primebuilddesign.co.uk/og-image.jpg",
        },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://primebuilddesign.co.uk/blog/${post.slug}` },
    },
    buildBreadcrumbs([
      { name: "Insights", path: "/blog" },
      { name: post.title, path: `/blog/${post.slug}` },
    ]),
  ];

  return (
    <div data-testid={`blog-post-${post.slug}`}>
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.hero_image || undefined}
        jsonLd={jsonLd}
      />

      <article>
        {/* HERO */}
        <section className="bg-slate-50 border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-28 md:pb-16">
            <Link to="/blog" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="blog-post-back">
              <ArrowLeft size={14} /> All insights
            </Link>
            {post.category && (
              <div className="overline mt-8 text-[#C5A059]">{post.category}</div>
            )}
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05] mt-4">
              {post.title}
            </h1>
            <p className="mt-6 text-xl text-slate-600 leading-relaxed">{post.excerpt}</p>
            <div className="mt-8 text-[11px] tracking-[0.16em] uppercase text-slate-500">
              {post.author} · {new Date(post.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}
            </div>
          </div>
        </section>

        {post.hero_image && (
          <section className="bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
              <img
                src={post.hero_image}
                alt={post.title}
                className="w-full h-auto border border-slate-200"
                loading="eager"
                decoding="async"
              />
            </div>
          </section>
        )}

        {/* BODY */}
        <section className="bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20" data-testid="blog-post-body">
            <Markdown text={post.body} />
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#C5A059]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-8 items-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
              Planning a project of your own?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
              <Link to="/contact" className="bg-slate-900 text-white px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-800 transition" data-testid="blog-post-cta-contact">
                Request a Free Quote <ArrowUpRight size={18} />
              </Link>
              <Link to="/estimator" className="bg-white text-slate-900 px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-50 transition" data-testid="blog-post-cta-estimator">
                Try AI Estimator <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
}
